// react core
import { useContext, useState, useEffect, useCallback } from "react";

// pull to refresh
// import PullToRefresh from "react-simple-pull-to-refresh";

// material design
import Box from "@mui/material/Box";
import Grow from "@mui/material/Grow";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";

// entzy config models and components
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { HeaderTag, PayTag } from "components/utils/common/CommonTags";
import { UserAvatar } from "components/utils/common/CommonAvatars";
// import { ActionBoxButton } from "components/utils/common/CommonButtons";
import {
  ActionAlert,
  LineLoader,
  // ActionLoader,
  // PullLoader,
  // PullPrompt,
} from "components/utils/common/CommonLoaders";
import { getCombinedUserId } from "models/Tools";

// entzy direct messaging components
import InputForm from "components/input/InputForm";
import MemberContacts from "components/members/MemberContacts";
import MemberMessaging from "components/member/MemberMessaging";
import MemberMessagingSelector from "components/member/MemberMessagingSelector";
import MemberPostMessage from "components/member/MemberPostMessage";

// fonts and colors
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus as iconNewChat } from "@fortawesome/pro-thin-svg-icons";
import { faUserPlus as iconAddContact } from "@fortawesome/pro-thin-svg-icons";
import { faPaperPlane as iconPost } from "@fortawesome/pro-thin-svg-icons";
import { faRefresh as iconRefresh } from "@fortawesome/pro-thin-svg-icons";
import { faTimes as iconClose } from "@fortawesome/pro-thin-svg-icons";

function MemberProfile(props) {
  const mainContext = useContext(MainContext);
  const user = props.user;
  const memberName = props.memberName;
  const [roomArea, setRoomArea] = useState(null);
  const [roomName, setRoomName] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [showPost, setShowPost] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleOpenAction = () => {
    setShowPost(true);
  };
  const handleCloseAction = () => {
    setShowPost(false);
  };
  const activateRoom = (roomArea, roomName, roomId) => {
    mainContext.openSubscription("memberMessaging", {
      RoomArea: roomArea,
      RoomName: roomName,
    });
  };
  const cbActivateRoom = useCallback(activateRoom, [mainContext]);

  const handleOpenContacts = (params) => {
    const userInteraction = mainContext.state.interaction.users.find(
      (obj) => obj.name === params.name
    );
    if (userInteraction) {
      mainContext.setMemberContactViewer({
        open: mainContext.state.memberContact.viewer.open ? false : true,
        user: userInteraction,
      });
    }
  };

  const handleShowNewChat = () => {
    setShowNewChat(!showNewChat);
  };

  const handlePayDirect = async () => {
    if (user.connected) {
      mainContext.setPayDirectViewer({
        open: mainContext.state.payDirect.viewer.open ? false : true,
        recipient: "@" + memberName,
        // currency: "usd",
      });
      mainContext.setMemberMessageViewer({
        open: false,
      });
    } else {
      return true;
    }
  };

  const handleRefresh = async () => {
    if (user.connected) {
      if (roomId) {
        setLoading(true);
        const response = await Promise.all([
          mainContext.preparePullNotifications({
            Category: null,
          }),
          mainContext.preparePullMemberMessages({
            RoomArea: roomArea,
            RoomName: roomName,
          }),
        ]);
        if (response.filter((item) => item.alert).length > 0) {
          mainContext.updateAlert({
            alert: true,
            message:
              "Hmm unable to refresh direct messages on that try. Give it another go or contact us if this problem persists.",
          });
        } else {
          mainContext.pullNotifications(response[0]);
          mainContext.pullMemberMessages({
            roomId: roomId,
            data: response[1].data,
          });
        }
        const responseNext = await Promise.all([
          mainContext.preparePullMemberChatSettings({
            RoomArea: roomArea,
            RoomName: roomName,
          }),
        ]);
        if (responseNext.filter((item) => item.alert).length > 0) {
          mainContext.updateAlert({
            alert: true,
            message:
              "Hmm unable to refresh direct message settings on that try. Give it another go or contact us if this problem persists.",
          });
        } else {
          mainContext.pullMemberChatSettings({
            roomId: roomId,
            data: responseNext[0].data,
          });
        }
        setLoading(false);
      } else {
        mainContext.updateAlert({
          alert: true,
          message: "Unable to refresh direct messages. Member not found.",
        });
      }
    }
    return true;
  };

  const handleNewChatActionCallback = async (data) => {
    const memberName = data.filter((obj) => obj.id === "member-name")[0].value;
    if (memberName.length === 0) {
      return {
        alert: true,
        message: "Please enter a member name",
      };
    }
    props.setMemberName(memberName);
    return {
      alert: false,
    };
  };

  // add effect to load direct messaging data when user interaction is created
  useEffect(() => {
    // set a timeout to deactivate loading if no user interaction found
    if (!memberName || memberName.length === 0) {
      setLoading(false);
      return;
    }
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 2000);
    // wait for interaction to be set by avatar loading
    const userInteraction = mainContext.state.interaction.users.filter(
      (obj) => obj.name === memberName
    );
    if (userInteraction.length > 0) {
      const area = getCombinedUserId(
        user.identity,
        userInteraction[0].identity
      );
      const name = "main";
      const id = area + configEntzy.DELIMITER_DEFAULT + name;
      setRoomArea(area);
      setRoomName(name);
      setRoomId(id);
      setLoading(false);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [mainContext.state.interaction.users, memberName, user.identity]);

  // add effect to open direct messaging subscription when room id is set
  useEffect(() => {
    if (roomArea && roomName && roomId) {
      cbActivateRoom(roomArea, roomName, roomId);
    }
  }, [cbActivateRoom, roomArea, roomName, roomId]);

  return (
    <Box className="box-default">
      {/* <PullToRefresh
        onRefresh={handleRefresh}
        refreshingContent={<PullLoader />}
        pullingContent={<PullPrompt />}
      >
      </PullToRefresh> */}
      <Box
        className="box-default"
        sx={{
          pb: configEntzy.APP_SPACING_HL3X,
        }}
      >
        <HeaderTag text="Direct Messaging" />
        <Box
          className="box-default"
          // sx={{
          //   pl: configEntzy.APP_SPACING_MD,
          //   pr: configEntzy.APP_SPACING_MD,
          // }}
        >
          {user.connected && roomId && (
            <MemberMessagingSelector
              {...props}
              roomArea={roomArea}
              roomName={roomName}
              roomId={roomId}
              memberName={memberName}
              setMemberName={props.setMemberName}
            />
          )}
        </Box>

        <Box
          className="box-default"
          // sx={{
          //   px: configEntzy.APP_SPACING_MD,
          // }}
        >
          <Box
            className="box-default bg-black-t50"
            sx={{
              mt: configEntzy.APP_SPACING_SM,
              p: configEntzy.APP_SPACING_MD,
              // height: configEntzy.AVATAR_SIZE_LG,
              borderRadius: configEntzy.BORDER_SIZE_XL,
            }}
          >
            <Box className="box-default">
              <Box
                className="box-inline left"
                sx={{ pr: configEntzy.APP_SPACING_SM }}
              >
                <UserAvatar
                  user={user}
                  size="sm"
                  avatarOnly={true}
                  avatarBorder="#333"
                />
              </Box>
              <Box
                className="box-inline left"
                sx={{ pr: configEntzy.APP_SPACING_SM }}
              >
                <UserAvatar
                  user={{ name: memberName }}
                  size="sm"
                  avatarOnly={true}
                  avatarBorder="#333"
                />
              </Box>

              {roomId &&
                mainContext.state.contactList.data.items.filter(
                  (member) => member.User.name === memberName && member.contact
                ).length === 0 && (
                  <Box
                    className="box-inline left bg-black-t90 shadow-darker action-pointer"
                    sx={{
                      width: configEntzy.AVATAR_SIZE_XS,
                      height: configEntzy.AVATAR_SIZE_XS,
                      mr: configEntzy.APP_SPACING_SM,
                      borderRadius: configEntzy.BORDER_SIZE_XL,
                      backgroundColor: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => handleOpenContacts({ name: memberName })}
                  >
                    <Typography variant="subtitle2">
                      <FontAwesomeIcon
                        icon={iconAddContact}
                        // size="2x"
                        color="white"
                      />
                    </Typography>
                  </Box>
                )}

              {!roomId && (
                <Box
                  className="box-inline right bg-black-t90 shadow-darker action-pointer"
                  sx={{
                    width: configEntzy.AVATAR_SIZE_SM,
                    height: configEntzy.AVATAR_SIZE_SM,
                    ml: configEntzy.APP_SPACING_SM,
                    borderRadius: configEntzy.BORDER_SIZE_XL,
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={handleShowNewChat}
                >
                  <Typography variant="subtitle2">
                    <FontAwesomeIcon
                      icon={showNewChat ? iconClose : iconNewChat}
                      size="2x"
                      color="white"
                    />
                  </Typography>
                </Box>
              )}

              {roomId && (
                <Box
                  className="box-inline right bg-black-t90 shadow-darker action-pointer"
                  sx={{
                    width: configEntzy.AVATAR_SIZE_SM,
                    height: configEntzy.AVATAR_SIZE_SM,
                    ml: configEntzy.APP_SPACING_SM,
                    borderRadius: configEntzy.BORDER_SIZE_XL,
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={showPost ? handleCloseAction : handleOpenAction}
                >
                  <Typography variant="subtitle2">
                    <FontAwesomeIcon
                      icon={showPost ? iconClose : iconPost}
                      size="2x"
                      color="white"
                    />
                  </Typography>
                </Box>
              )}

              {roomId && (
                <Box
                  className="box-inline right bg-black-t90 shadow-darker action-pointer"
                  sx={{
                    width: configEntzy.AVATAR_SIZE_SM,
                    height: configEntzy.AVATAR_SIZE_SM,
                    ml: configEntzy.APP_SPACING_SM,
                    borderRadius: configEntzy.BORDER_SIZE_XL,
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={handleRefresh}
                >
                  <Typography variant="subtitle2">
                    <FontAwesomeIcon
                      icon={iconRefresh}
                      size="2x"
                      color="white"
                    />
                  </Typography>
                </Box>
              )}

              {roomId && (
                <Box
                  className="box-inline right bg-black-t90 shadow-darker action-pointer"
                  sx={{
                    width: configEntzy.AVATAR_SIZE_SM,
                    height: configEntzy.AVATAR_SIZE_SM,
                    ml: configEntzy.APP_SPACING_SM,
                    borderRadius: configEntzy.BORDER_SIZE_XL,
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PayTag onClick={handlePayDirect} />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {loading && (
          <Box className="box-default relative pop-up zero-height">
            <Box
              className="box-default"
              sx={{
                mt: configEntzy.APP_SPACING_XS3X,
                px: configEntzy.APP_SPACING_MD2X,
                opacity: 0.5,
              }}
            >
              <LineLoader />
            </Box>
          </Box>
        )}

        {memberName && !loading && !roomId && (
          <Box
            className="box-default"
            sx={{
              mt: configEntzy.APP_SPACING_MD,
              mb: configEntzy.APP_SPACING_SM,
              px: configEntzy.APP_SPACING_MD2X,
            }}
          >
            <Box
              className="box-default bg-black-t90"
              sx={{
                p: configEntzy.APP_SPACING_MD,
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <ActionAlert
                severity="warning"
                title="Member Messaging"
                message={"@" + memberName + " not found"}
              />
            </Box>
          </Box>
        )}

        <Grow in={showNewChat}>
          <Box
            className="box-default"
            sx={{
              pt: configEntzy.APP_SPACING_MD2X,
              px: configEntzy.APP_SPACING_MD2X,
            }}
            hidden={!showNewChat}
          >
            <Box className="box-default">
              <Typography variant="subtitle2">
                Enter the member name you wish to message below
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ mb: configEntzy.APP_SPACING_MD2X }}
              >
                User @name is required to start a DM conversation
              </Typography>
            </Box>
            <Box className="box-default">
              <InputForm
                navigate={props.navigate}
                fields={[
                  {
                    id: "member-name",
                    type: "text-standard",
                    placeholder: "Enter member name",
                    value: "",
                    required: true,
                    alphanumeric: true,
                    maxLength: 100,
                    disabled: !user.connected,
                    startAdornment: "@",
                  },
                ]}
                submitText="Open New Chat"
                disabledSubmit={!user.connected}
                disabledTitle="Connect to continue"
                disabledText="Tap here to activate messaging"
                disabledClick={props.drawerConnectToggle}
                callback={handleNewChatActionCallback}
              />
            </Box>
          </Box>
        </Grow>

        {roomId && (
          <Box className="box-default" sx={{ mt: configEntzy.APP_SPACING_MD }}>
            <MemberMessaging
              {...props}
              roomArea={roomArea}
              roomName={roomName}
              roomId={roomId}
              memberName={memberName}
              handlePostMemberMessage={
                showPost ? handleCloseAction : handleOpenAction
              }
            />
          </Box>
        )}

        {!roomId && (
          <Box className="box-default" sx={{ px: configEntzy.APP_SPACING_MD }}>
            <MemberContacts {...props} />
          </Box>
        )}

        <Drawer anchor="bottom" open={showPost} onClose={handleCloseAction}>
          <Box className="box-default">
            <MemberPostMessage
              {...props}
              roomArea={roomArea}
              roomName={roomName}
              roomId={roomId}
              memberName={memberName}
              handleOpenAction={handleOpenAction}
              handleCloseAction={handleCloseAction}
            />
          </Box>
        </Drawer>
        {/* Observables idle alert */}
        <Box
          className="box-fixed bottom-left full-width"
          sx={{
            p: configEntzy.APP_SPACING_LG,
            textAlign: "left",
          }}
          hidden={
            !user.connected || mainContext.state.observablesTracker.active
          }
        >
          <ActionAlert
            severity="info"
            title="Sleeping"
            message={
              "Move around to wake things up. Sleep mode entered after " +
              configEntzy.IDLE_TIMEOUT_MINS +
              " minutes of inactivity"
            }
          />
        </Box>
      </Box>
    </Box>
  );
}

export default MemberProfile;
